import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import RadioGroup from '@material-ui/core/RadioGroup';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';

import { stripeBasePlansSelector } from '../../account-selectors';
import { getStripeBasePlans } from '../../actions/get-stripe-base-plans';

import { SubscriptionCadence } from '../../../util/subscription-cadence';
import { LabeledRadioButton } from '../../../widgets/labeled-radio-button';

type InputProps = {
  subscriptionCadence?: SubscriptionCadence | null;
  baseMonthlyRateStripePlanId: string | null;
  accountMissingPaymentInfo?: boolean | null;
  onChange: (
    subscriptionCadence: SubscriptionCadence | null,
    baseMonthlyRateStripePlanId: string | null,
  ) => void;
};

const mapStateToProps = (state: any) => ({
  basePlans: stripeBasePlansSelector(state),
});

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    getStripeBasePlans: () => dispatch(getStripeBasePlans()),
  },
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & StateProps & DispatchProps;

export const LegacyBasePlanSelectorFormBody = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  ({
    subscriptionCadence,
    baseMonthlyRateStripePlanId,
    accountMissingPaymentInfo,
    basePlans,
    actions,
    onChange,
  }: Props) => {
    // If there is no subscription cadence, but it is selectable, default to monthly
    const [selectedSubscriptionCadence, setSelectedSubscriptionCadence] =
      useState<SubscriptionCadence | null>(
        subscriptionCadence ??
          (typeof subscriptionCadence !== 'undefined' ? SubscriptionCadence.MONTHLY : null),
      );
    const [selectedBaseMonthlyRateStripePlanId, setSelectedBaseMonthlyRateStripePlanId] = useState<
      string | null
    >(baseMonthlyRateStripePlanId);

    useEffect(() => {
      actions.getStripeBasePlans();
    }, [actions]);

    useEffect(() => {
      onChange(selectedSubscriptionCadence, selectedBaseMonthlyRateStripePlanId);
    }, [selectedSubscriptionCadence, selectedBaseMonthlyRateStripePlanId, onChange]);

    const changeCadence = (event: React.ChangeEvent<HTMLInputElement>) => {
      const eventValue = event.target.value as SubscriptionCadence;
      setSelectedSubscriptionCadence(eventValue);
    };

    const handleRateChange = (
      event: React.ChangeEvent<{ name?: string | undefined; value: unknown }>,
    ) => {
      const eventValue = event.target.value as string;
      setSelectedBaseMonthlyRateStripePlanId(eventValue);
    };

    if (!basePlans) {
      return <Typography variant="body2">Loading...</Typography>;
    }

    return (
      <>
        {subscriptionCadence !== undefined && (
          <FormControl margin="dense" className="u-width-100">
            <FormHelperText className="u-margin-top-xs">Cadence</FormHelperText>
            <RadioGroup
              value={selectedSubscriptionCadence ?? SubscriptionCadence.MONTHLY}
              onChange={changeCadence}
              className="u-margin-y-md"
              name="base-cadence"
            >
              <LabeledRadioButton
                value={SubscriptionCadence.MONTHLY}
                label="Billed Monthly"
                subtext={`(${
                  accountMissingPaymentInfo ? 'Credit card information will be collected; ' : ''
                }charged automatically)`}
              />
              <LabeledRadioButton
                value={SubscriptionCadence.ANNUAL}
                label="Billed Annually"
                subtext="(charged separately)"
              />
              <LabeledRadioButton
                value={SubscriptionCadence.OTHER}
                label="Other"
                subtext="(rarely used)"
              />
            </RadioGroup>
          </FormControl>
        )}
        <FormControl margin="dense" className="u-width-100">
          <InputLabel htmlFor="base-stripe-plan-id" className="u-width-250px" shrink>
            Monthly Rate (Ignoring discounts)
          </InputLabel>
          <Select
            value={selectedBaseMonthlyRateStripePlanId ?? ''}
            onChange={handleRateChange}
            displayEmpty
            className="u-width-100"
            inputProps={{
              name: 'baseStripePlanId',
              id: 'base-stripe-plan-id',
            }}
          >
            {basePlans
              .sort((a, b) => a.amount - b.amount)
              .map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.nickname}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </>
    );
  },
);
